// The Code workspace's icon set: 16px, 1.3px strokes, `currentColor`, the
// same construction as the viewport toolbar's VtIcon (editor-panels.tsx) so
// the two toolbars read as one family.
import type { ReactElement } from 'react';

export type CodeIconKind =
  | 'play'
  | 'stop'
  | 'plus'
  | 'close'
  | 'more'
  | 'search'
  | 'format'
  | 'bolt'
  | 'file'
  | 'function'
  | 'record'
  | 'constant'
  | 'variable'
  | 'uses'
  | 'import'
  | 'error'
  | 'warning'
  | 'info'
  | 'check'
  | 'trash'
  | 'copy'
  | 'pencil'
  | 'book'
  | 'symbol'
  | 'wrap'
  | 'map'
  | 'clear'
  | 'arrow'
  | 'terminal';

const PATHS: Record<CodeIconKind, ReactElement> = {
  play: <path d="M4.5 2.8v10.4l8-5.2-8-5.2z" fill="currentColor" stroke="none" />,
  stop: <rect x="3.5" y="3.5" width="9" height="9" rx="1.5" fill="currentColor" stroke="none" />,
  plus: <path d="M8 3v10M3 8h10" />,
  close: <path d="M4 4l8 8M12 4l-8 8" />,
  more: <g fill="currentColor" stroke="none"><circle cx="8" cy="3.2" r="1.3" /><circle cx="8" cy="8" r="1.3" /><circle cx="8" cy="12.8" r="1.3" /></g>,
  search: <g><circle cx="7" cy="7" r="4.2" /><path d="M10.2 10.2L13.5 13.5" /></g>,
  format: <path d="M2.5 4h11M2.5 8h7M2.5 12h9" />,
  bolt: <path d="M9 1.5L3.5 9h4l-.8 5.5L12.5 7h-4L9 1.5z" strokeLinejoin="round" />,
  file: <g><path d="M4 2h5.5L13 5.5V14H4z" strokeLinejoin="round" /><path d="M9.5 2v3.5H13" /></g>,
  function: <path d="M10.5 2.5c-2 0-2.8 1-3 2.6L6.8 11c-.3 1.7-1 2.5-2.8 2.5M4.5 7.5h5" strokeLinecap="round" />,
  record: <path d="M5.5 2.5c-1.5 0-2 .7-2 2v2c0 .8-.4 1.5-1.2 1.5.8 0 1.2.7 1.2 1.5v2c0 1.3.5 2 2 2M10.5 2.5c1.5 0 2 .7 2 2v2c0 .8.4 1.5 1.2 1.5-.8 0-1.2.7-1.2 1.5v2c0 1.3-.5 2-2 2" strokeLinecap="round" />,
  constant: <g><rect x="2.5" y="2.5" width="11" height="11" rx="2.5" /><path d="M5.5 8h5" /></g>,
  variable: <g><rect x="2.5" y="2.5" width="11" height="11" rx="2.5" /><path d="M5.5 5.5l5 5M10.5 5.5l-5 5" /></g>,
  uses: <g><circle cx="8" cy="8" r="5.5" /><path d="M8 5v3.5l2.5 1.5" /></g>,
  import: <path d="M8 2.5v8M4.8 7.3L8 10.5l3.2-3.2M3 13.5h10" strokeLinecap="round" strokeLinejoin="round" />,
  error: <g><circle cx="8" cy="8" r="5.8" /><path d="M5.8 5.8l4.4 4.4M10.2 5.8l-4.4 4.4" /></g>,
  warning: <g><path d="M8 2.2L14 13H2L8 2.2z" strokeLinejoin="round" /><path d="M8 6.2v3.3" /><circle cx="8" cy="11.3" r="0.6" fill="currentColor" stroke="none" /></g>,
  info: <g><circle cx="8" cy="8" r="5.8" /><path d="M8 7.2v3.8" /><circle cx="8" cy="5" r="0.6" fill="currentColor" stroke="none" /></g>,
  check: <path d="M3 8.5l3.2 3L13 4.5" strokeLinecap="round" strokeLinejoin="round" />,
  trash: <g><path d="M3 4.5h10M6.5 4.5V3h3v1.5M4.5 4.5l.7 8.5h5.6l.7-8.5" strokeLinejoin="round" /></g>,
  copy: <g><rect x="5.5" y="5.5" width="8" height="8" rx="1.5" /><path d="M10.5 5.5V3.8A1.3 1.3 0 0 0 9.2 2.5H3.8A1.3 1.3 0 0 0 2.5 3.8v5.4a1.3 1.3 0 0 0 1.3 1.3h1.7" /></g>,
  pencil: <path d="M3 13l.8-3.2 7.4-7.4 2.4 2.4-7.4 7.4L3 13zM9.8 3.8l2.4 2.4" strokeLinejoin="round" />,
  book: <g><path d="M2.5 3.2c2-.9 3.7-.9 5.5.3 1.8-1.2 3.5-1.2 5.5-.3v9.6c-2-.9-3.7-.9-5.5.3-1.8-1.2-3.5-1.2-5.5-.3V3.2z" strokeLinejoin="round" /><path d="M8 3.5v9.3" /></g>,
  symbol: <path d="M3 4.5h10M3 8h6M3 11.5h8" strokeLinecap="round" />,
  wrap: <path d="M2.5 4h11M2.5 8h8.5a2 2 0 0 1 0 4H9M2.5 12h4M10.3 10.5L9 12l1.3 1.5" strokeLinecap="round" strokeLinejoin="round" />,
  map: <g><rect x="2.5" y="2.5" width="11" height="11" rx="1.5" /><path d="M10 2.5v11M5 5.5h2.5M5 8h2.5M5 10.5h2.5" /></g>,
  clear: <path d="M3 3.5h10M5 3.5l.6 9h4.8l.6-9M6.5 3.5V2.5h3v1" strokeLinejoin="round" />,
  arrow: <path d="M3 8h9M8.5 4.5L12 8l-3.5 3.5" strokeLinecap="round" strokeLinejoin="round" />,
  terminal: <g><rect x="2" y="3" width="12" height="10" rx="1.5" /><path d="M4.5 6l2.5 2-2.5 2M8.5 10.5h3" strokeLinecap="round" strokeLinejoin="round" /></g>,
};

export function CodeIcon({ kind, size = 15 }: { kind: CodeIconKind; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.3">
      {PATHS[kind]}
    </svg>
  );
}
